import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportEibExpensesClassificators, reportEibFilterOptions } from "../report-eib";
import { ReportEibFilterSettingsForm } from "../settings-form/ReportEibFilterSettingsForm";
import { ReportEibFilterSettings } from "../settings/ReportEibFilterSettings";
import { allIncomeClassificators } from "../../../common/baseOptions";
import { ReportEibTableDataRequest } from "../data-request/ReportEibTableDataRequest";
import { ReportEibRow } from "../table-data/ReportEibRow";
import { ReportEibJournalRequest } from "../journal/ReportEibJournalRequest";
import { MutuallyRedeemingLevel, Segmentation } from "../../../common/types/roots/fields";
import { Option } from "../../../common/types/Option";

export class ReportEibFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportEibFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'EIB') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'EIB') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        const mutuallyRedeemingOption = cloneDeep(
            options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming) ?? null
        )

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        // 2. Формирование
        const form: ReportEibFilterSettingsForm = {
            // Base:
            key: 'EIB',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: new Date(settings.date),
            measureUnit: measureUnitOption,
            budgetTypes: budgetTypeOptions,
            region: regionOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            segmentations: cloneDeep(settings.segmentations ?? []),
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            sortByRegionCode: settings.sortByRegionCode ?? false,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'EIB') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportEibFilterSettings = {
            // Base:
            reportName: 'EIB',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            budgetTypes: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            mutuallyRedeeming: form.mutuallyRedeeming?.value ?? null,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: form.segmentations ?? [],
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses),
            sortByRegionCode: form.sortByRegionCode ?? false,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportEibFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportEibFilterSettingsForm = {
            key: 'EIB',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'EIB')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'MIXED')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields            
            date: yesterdayDate,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND') ?? null,
            budgetTypes: [],
            region: null,
            roundUp: false,
            roundUpTo: null,
            mutuallyRedeeming: null,
            mutuallyRedeemingLevels: [],
            segmentations: cloneDeep(options.segmentations),
            sortByRegionCode: false,
            incomes: cloneDeep(allIncomeClassificators).map(it => {
                it.active = true
                return it
            }),
            expenses: cloneDeep(reportEibExpensesClassificators).map(it => {
                if (['abp', 'prg', 'ppr'].includes(it.key)) it.active = true
                return it
            }),
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'EIB') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_input_budget_types')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.mutuallyRedeeming.value !== 'WITH') {
            if (form.mutuallyRedeemingLevels.length == 0) {
                throw new Error('error_input_mutually_redeeming_levels')
            }
        }

        const activeSegmentations: Segmentation[] = form.segmentations?.filter(it => it.active).map(it => it.value) ?? []

        if (!activeSegmentations.length) {
            throw new Error('error_input_segmentation')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const activeExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        const activeIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const levels: (3 | 4)[] = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            }
            throw new Error('inappropriate mutually redeeming level')
        })

        const params: ReportEibTableDataRequest = {
            date: strDate,
            budget_types: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: levels,
            segmentations: activeSegmentations,

            incomes: activeIncomes,
            expenses: activeExpenses,
            sort_by_region_code: form.sortByRegionCode ?? false,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/eib',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: any[] = await response.json()

        const tableData: ReportEibRow[] = responseTableData.map((it: any, i: number) => {
            const daysKeys = Object.keys(it)
                .filter((key) => !isNaN(Number(key)))
                .sort((a, b) => Number(a) - Number(b))

            const dayCompletions: number[] = daysKeys.map(key => it[key])

            const row: ReportEibRow = {
                index: it.index,
                parentIndex: it.parent_index ?? null,  // parent's idx, null if root
                visible: it.visible,      // whether the row is currently shown
                hasChild: it.has_child,     // whether this row has any children
                collapse: it.collapse,   

                region: it.region ?? null,
                kat: it.kat ?? null,
                cls: it.cls ?? null,
                pcl: it.pcl ?? null,
                spf: it.spf ?? null,

                eregion: it.eregion ?? null,
                gr: it.gr ?? null,
                pgr: it.pgr ?? null,
                abp: it.abp ?? null,
                prg: it.prg ?? null,
                ppr: it.ppr ?? null,
                espf: it.espf ?? null,

                name: it.name ?? null,

                yearPlan: it.year_plan,
                upToMonthPlan: it.up_to_month_plan,
                monthPlan: it.month_plan,

                upToMonthExecution: it.up_to_month_execution,
                dayExecution: it.day_execution,

                yearRemaining: it.year_remaining,
                periodRemining: it.period_remaining,

                yearCompletionPercentage: it.year_completion_percentage,
                periodCompletionPercentage: it.period_completion_percentage,

                dayCompletions: dayCompletions,

                monthCompletion: it.month_completion
            }

            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportEibJournalRequest = {
            reportName: "EIB",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            date: strDate,
            measure_unit: form.measureUnit.value,
            incomes_order: form.incomes.filter(it => it.active).map(it => it.key),
            expenses_order: form.expenses.filter(it => it.active).map(it => it.key),
            segmentations: activeSegmentations,
            table_data: responseTableData
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
